import React, { useCallback, useEffect, useMemo } from 'react';
import { MultiSelectConfig } from '@kemu-io/kemu-core/types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import { Dropdown, Menu, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { DataType } from '@kemu-io/hs-types';
import FieldLabel from '../FieldLabel/FieldLabel';
import styles from './MultiSelectField.module.css';
import FormGroup from '@components/form-control/formGroup/formGroup';
import useForceUpdate from '@hooks/useForceReload';

type Props = {
  label: string;
  variableName: string;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  color?: string;
  disabled?: boolean;
  isSingleElement: boolean;
  config: MultiSelectConfig;
  settingsIndex: number;
  onUpdateConfig: (settingsIndex: number, newConfig: MultiSelectConfig) => void;
}

type MenuInfo = {
  key: string;
}

const MultiSelectField = (props: Props) => {
  const { variableName, widgetId, disabled, isSingleElement, label, config, settingsIndex, onUpdateConfig } = props;
  const scopedVarName = getControlScopedVarName(variableName);
  const forceUpdate = useForceUpdate();

  const variableValue = variablesManager.getVariableValue({
    variableName: scopedVarName,
    readerWidgetId: widgetId,
  });

  const selectedOptions = (variableValue?.value as string[] | undefined) ?? config.customValue ?? config.defaultValue ?? [];

  const handleMenuClick = useCallback((event: MenuInfo) => {
    if (event.key) {
      const option = String(event.key);
      let final: string[];
      if (selectedOptions.includes(option)) {
        final = selectedOptions.filter((c) => c !== option);
      } else {
        final = [...selectedOptions, option];
      }

      variablesManager.setVariableValue({
        variableName: scopedVarName,
        value: final,
        setByWidgetId: widgetId,
        type: DataType.Array,
      });

      onUpdateConfig(settingsIndex, {
        ...config,
        customValue: final,
      } as MultiSelectConfig);
    }
  }, [scopedVarName, widgetId, onUpdateConfig, settingsIndex, config, selectedOptions]);

  const menu = useMemo(() => (
		<Menu
      onClick={handleMenuClick}
      className={styles.Menu}
      selectedKeys={selectedOptions}
    >
			{config.options.map((option) => (
				<Menu.Item
          key={`${option}`}
          className={classNames(styles.DropdownItem, {
            [styles.NotSelected]: !selectedOptions.includes(option),
          })}
          style={{
            backgroundColor: selectedOptions.includes(option) ? props.color : undefined,
            color: selectedOptions.includes(option) ? 'white' : undefined,
          }}
        >
					{option}
				</Menu.Item>
			))}
		</Menu>
	), [config.options, selectedOptions, handleMenuClick, props.color]);


  const allSelectedLabels = selectedOptions.join(', ');
  let visibleSelectedLabels = allSelectedLabels;
  if (selectedOptions.length > 2) {
    visibleSelectedLabels = `${selectedOptions[0]}, ${selectedOptions[1]}...`;
  }

  // Subscribe to variable changes to trigger re-render
  useEffect(() => {
    const unsubscribe = variablesManager.onVariableValueChange({
      variableName: scopedVarName,
      type: DataType.Anything,
      widgetId: widgetId,
      onValueChange: forceUpdate,
    });

    return unsubscribe;
  }, [scopedVarName, widgetId, forceUpdate]);

  return (
    <FormGroup column={true} noMarginBottom>
      <FieldLabel color={props.color} label={label} />
      <Dropdown
        overlay={menu}
        placement="bottomLeft"
        trigger={['click']}
        disabled={disabled}
        className={classNames({
          [styles.SingleDropDown]: isSingleElement && !label,
        })}
      >
        <div className={styles.DropDownTrigger}>
          <Tooltip title={allSelectedLabels} mouseEnterDelay={0.6}>
            <span className={styles.SelectedOption} >
              {visibleSelectedLabels}
            </span>
          </Tooltip>
          <DownOutlined
            className={styles.DropIcon}
          />
        </div>
      </Dropdown>
    </FormGroup>
  );
};

export default MultiSelectField;
